import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Search, MapPin, Clock, Video, Loader2, AlertTriangle, RefreshCw, CalendarDays, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PAGE_SIZE = 40;

const AA_BLUE = "#0779bf";
const AA_NAVY = "#03476f";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const AttendanceBadge = ({ attendance }) => {
  const map = {
    in_person: { label: "In person", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
    online: { label: "Online", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30" },
    hybrid: { label: "Hybrid", cls: "bg-violet-500/10 text-violet-700 border-violet-500/30" },
    inactive: { label: "Inactive", cls: "bg-black/[0.05] text-[#6e6e6e] border-black/10" },
  };
  const v = map[attendance] || map.in_person;
  return (
    <span className={`rounded-[5px] border px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em] ${v.cls}`}>
      {v.label}
    </span>
  );
};

const MeetingCard = ({ m, typeLabels }) => {
  const types = (m.types || []).filter((t) => t !== "ONL" && t !== "TC");
  const shown = types.slice(0, 4);
  return (
    <div
      data-testid={`aa-meeting-card-${m.id}`}
      className="rounded-[12px] border border-[#d8e8f3] bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md"
    >
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <div className="flex items-center gap-1.5 rounded-[8px] px-2.5 py-1.5" style={{ background: "#e2f0fa" }}>
          <Clock size={13} style={{ color: AA_BLUE }} />
          <span className="text-[12.5px] font-extrabold" style={{ color: AA_NAVY }}>
            {m.day !== null && m.day !== undefined ? `${DAYS_SHORT[m.day]} · ` : ""}{m.time_formatted || m.time}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-extrabold" style={{ color: AA_NAVY }} data-testid="aa-meeting-name">{m.name}</p>
          <p className="mt-0.5 flex items-start gap-1 text-[12.5px] text-[#54697a]">
            <MapPin size={12} className="mt-0.5 shrink-0 text-[#8fb4cd]" />
            <span className="min-w-0">
              {m.location ? <span className="font-bold">{m.location} · </span> : null}
              {m.address || "Address available at meeting time"}
            </span>
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <AttendanceBadge attendance={m.attendance} />
          {m.region && (
            <span className="rounded-[5px] border border-[#c6ddee] bg-[#eef6fb] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em]" style={{ color: AA_BLUE }}>
              {m.region}
            </span>
          )}
        </div>
      </div>
      {(shown.length > 0 || m.conference_url) && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {shown.map((t) => (
            <span key={t} className="rounded-full border border-black/10 bg-black/[0.03] px-2 py-0.5 text-[10px] font-bold text-[#54697a]">
              {typeLabels[t] || t}
            </span>
          ))}
          {types.length > 4 && (
            <span className="text-[10px] font-bold text-[#8fb4cd]">+{types.length - 4} more</span>
          )}
          {m.conference_url && (
            <a
              href={m.conference_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="aa-meeting-join-link"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-wide transition-opacity duration-150 hover:opacity-75"
              style={{ color: AA_BLUE }}
            >
              <Video size={12} /> Join Online
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export const MeetingFinder = () => {
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(false);

  const [q, setQ] = useState("");
  const [day, setDay] = useState(() => new Date().getDay()); // default: today
  const [region, setRegion] = useState("all");
  const [mtype, setMtype] = useState("all");
  const [attendance, setAttendance] = useState("all");

  const [meetings, setMeetings] = useState(null);
  const [total, setTotal] = useState(0);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const debounceRef = useRef(null);

  const loadMeta = useCallback(async () => {
    setMetaError(false);
    try {
      const { data } = await axios.get(`${API}/aa/meta`);
      setMeta(data);
    } catch (e) {
      setMetaError(true);
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const buildParams = useCallback((offset) => {
    const params = { limit: PAGE_SIZE, offset };
    if (day !== "all") params.day = day;
    if (region !== "all") params.region = region;
    if (mtype !== "all") params.type = mtype;
    if (attendance !== "all") params.attendance = attendance;
    if (q.trim()) params.q = q.trim();
    return params;
  }, [day, region, mtype, attendance, q]);

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get(`${API}/aa/meetings`, { params: buildParams(0) });
      setMeetings(data.meetings);
      setTotal(data.total);
      setFetchedAt(data.fetched_at);
    } catch (e) {
      setError(true);
      setMeetings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // debounce only the text search; other filters fire immediately
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchMeetings, q ? 300 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [fetchMeetings, q]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/aa/meetings`, { params: buildParams(meetings.length) });
      setMeetings((prev) => [...prev, ...data.meetings]);
    } catch (e) {
      /* keep current list */
    } finally {
      setLoadingMore(false);
    }
  };

  const typeLabels = meta?.type_labels || {};
  const topTypes = (meta?.types || []).slice(0, 14);

  return (
    <div data-testid="aa-meeting-finder" className="rounded-[16px] border border-[#c6ddee] bg-[#f7fbfe] p-4 shadow-sm sm:p-6">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="min-w-[200px] flex-1 space-y-1">
          <label className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#54697a]">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8fb4cd]" />
            <Input
              data-testid="aa-finder-search-input"
              placeholder="Group name, town, or address…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border-[#c6ddee] bg-white pl-9 focus-visible:ring-[#0779bf]"
            />
          </div>
        </div>
        <div className="w-[160px] space-y-1">
          <label className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#54697a]">Town</label>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger data-testid="aa-finder-town-select" className="border-[#c6ddee] bg-white">
              <SelectValue placeholder="All towns" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All towns</SelectItem>
              {(meta?.regions || []).map((r) => (
                <SelectItem key={r.name} value={r.name}>{r.name} ({r.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[170px] space-y-1">
          <label className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#54697a]">Meeting type</label>
          <Select value={mtype} onValueChange={setMtype}>
            <SelectTrigger data-testid="aa-finder-type-select" className="border-[#c6ddee] bg-white">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Any type</SelectItem>
              {topTypes.map((t) => (
                <SelectItem key={t.code} value={t.code}>{t.label} ({t.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[150px] space-y-1">
          <label className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#54697a]">Attendance</label>
          <Select value={attendance} onValueChange={setAttendance}>
            <SelectTrigger data-testid="aa-finder-attendance-select" className="border-[#c6ddee] bg-white">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              <SelectItem value="in_person">In person</SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="hybrid">Hybrid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Day pills */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="aa-finder-day-pills">
        <CalendarDays size={14} className="mr-0.5 text-[#8fb4cd]" />
        <button
          type="button"
          data-testid="aa-finder-day-all"
          onClick={() => setDay("all")}
          className={`rounded-full border px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wide transition-colors duration-150 ${day === "all" ? "border-transparent text-white" : "border-[#c6ddee] bg-white text-[#54697a] hover:border-[#0779bf]"}`}
          style={day === "all" ? { background: AA_BLUE } : {}}
        >
          All days
        </button>
        {DAYS_SHORT.map((d, i) => (
          <button
            key={d}
            type="button"
            data-testid={`aa-finder-day-${i}`}
            onClick={() => setDay(i)}
            className={`rounded-full border px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wide transition-colors duration-150 ${day === i ? "border-transparent text-white" : "border-[#c6ddee] bg-white text-[#54697a] hover:border-[#0779bf]"}`}
            style={day === i ? { background: AA_BLUE } : {}}
          >
            {d}
          </button>
        ))}
      </div>

      {/* Result count + freshness */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Users size={14} style={{ color: AA_BLUE }} />
        <p data-testid="aa-finder-result-count" className="text-[13px] font-extrabold" style={{ color: AA_NAVY }}>
          {loading ? "Searching…" : `${total.toLocaleString()} meeting${total === 1 ? "" : "s"} found`}
        </p>
        {fetchedAt && !loading && (
          <span className="text-[11px] text-[#8fb4cd]">· live Area 11 registry data, updated {timeAgo(fetchedAt)}</span>
        )}
        {metaError && (
          <button type="button" onClick={loadMeta} className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 hover:underline">
            <RefreshCw size={11} /> Reload filter options
          </button>
        )}
      </div>

      {/* Results */}
      <div className="mt-3 space-y-2.5">
        {loading ? (
          <>
            <Skeleton className="h-[92px] rounded-[12px] bg-[#dcebf5]" />
            <Skeleton className="h-[92px] rounded-[12px] bg-[#dcebf5]" />
            <Skeleton className="h-[92px] rounded-[12px] bg-[#dcebf5]" />
          </>
        ) : error ? (
          <div data-testid="aa-finder-error-state" className="flex flex-col items-center gap-2 rounded-[12px] border border-amber-500/30 bg-amber-500/[0.06] py-10 text-center">
            <AlertTriangle size={24} className="text-amber-500" />
            <p className="text-sm font-extrabold" style={{ color: AA_NAVY }}>Couldn't reach the meeting registry</p>
            <p className="max-w-sm text-[12.5px] text-[#54697a]">The live feed may be briefly unavailable. Your meeting is still happening — try again, or call 866-783-7712.</p>
            <Button type="button" data-testid="aa-finder-retry-button" onClick={fetchMeetings} size="sm" className="mt-1 rounded-[10px] text-white" style={{ background: AA_BLUE }}>
              <RefreshCw size={13} /> Try Again
            </Button>
          </div>
        ) : meetings.length === 0 ? (
          <div data-testid="aa-finder-empty-state" className="flex flex-col items-center gap-2 rounded-[12px] border border-dashed border-[#c6ddee] bg-white py-10 text-center">
            <Search size={24} className="text-[#c6ddee]" />
            <p className="text-sm font-extrabold" style={{ color: AA_NAVY }}>No meetings match those filters</p>
            <p className="max-w-sm text-[12.5px] text-[#54697a]">Try a different day or clear the town filter — with 1,900+ weekly meetings, one is always close.</p>
          </div>
        ) : (
          <>
            {meetings.map((m) => (
              <MeetingCard key={`${m.id}-${m.day}-${m.time}`} m={m} typeLabels={typeLabels} />
            ))}
            {meetings.length < total && (
              <div className="pt-1 text-center">
                <Button
                  type="button"
                  data-testid="aa-finder-load-more-button"
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="rounded-[10px] border-[#c6ddee] font-extrabold"
                  style={{ color: AA_BLUE }}
                >
                  {loadingMore ? <Loader2 size={13} className="animate-spin" /> : null}
                  Show more ({(total - meetings.length).toLocaleString()} remaining)
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
